// src/pages/Curso.tsx — com fluxo para Pós (EAD manual) e cursos ativos da API

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  CircularProgress,
  Fade,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { coordenadores } from '../../components/coordenadores';

interface UnidadeCurricular {
  unidadeCurricularId: number;
  nomeDaUnidadeCurricular: string;
  ordem: number;
  cargaHoraria: number;
  cargaHorariaMaxima: number;
}

interface Turma {
  id: number | string;
  nomeTurma: string;
  nomeUnidadeOperativa?: string;
  formaDeExecucao?: string;
  planoDoCursoId?: number;
  informacoesParaVendaOnline?: string;
  informacoesDoPlanoDoCurso?: {
    nome?: string;
    objetivos?: string;
    perfilProfissional?: string;
    tipoDeAcao?: string;
  };
  horariosSemAbreviacao?: string[];
  dataPeriodoExecucaoInicialFormatada?: string;
  dataPeriodoExecucaoFinalFormatada?: string;
  recursosFinanceiros?: Array<{
    linkMatriculaOnlineDoRecursoFinanceiroNovo?: string;
  }>;
  ofertas?: Array<{
    formasDePagamento?: Array<{
      disponivelParaWeb?: boolean;
      meioDePagamentoDescricao?: string;
      numeroDeParcelas?: number;
      valorDaParcela?: number;
      totalPagamento?: number;
    }>
  }>;
  linkInteresse?: string;
  linkInscricao?: string;        // preenchido para abertas (API) e opcionalmente no manual
  linkInscricaoEAD?: string;     // **manual Pós/EAD**: link de inscrição externo
}

// ===== Helpers =====
const currencyBRL = (n: number | string | undefined) => {
  const num = typeof n === 'string' ? Number(n) : (n ?? 0);
  return Number(num).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// tenta uma lista de URLs em ordem e retorna o primeiro sucesso
async function getWithFallback(paths: string[]): Promise<any> {
  let lastErr: any = null;
  for (const url of paths) {
    try {
      const resp = await axios.get(url, { timeout: 20000 });
      // eslint-disable-next-line no-console
      console.log('✅ carregado:', url);
      return resp.data;
    } catch (err) {
      lastErr = err;
      // eslint-disable-next-line no-console
      console.warn('falha em', url, err);
    }
  }
  // eslint-disable-next-line no-console
  console.error('Todas as tentativas falharam.', lastErr);
  throw lastErr;
}

const isPosGraduacao = (t: Turma): boolean => {
  const tipo = (t?.informacoesDoPlanoDoCurso?.tipoDeAcao || '').toLowerCase();
  if (tipo.startsWith('pós') || tipo.startsWith('pos')) return true;
  const nome = `${t?.nomeTurma || ''} ${t?.informacoesDoPlanoDoCurso?.nome || ''}`.toLowerCase();
  return (
    nome.includes('pós') || nome.includes('pos') ||
    nome.includes('pós-grad') || nome.includes('pos-grad') ||
    nome.includes('especializa') || nome.includes('mba')
  );
};

const Curso: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [curso, setCurso] = useState<Turma | null>(null);
  const [matrizCurricular, setMatrizCurricular] = useState<UnidadeCurricular[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMatriz, setLoadingMatriz] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isTurmaEmBreve, setIsTurmaEmBreve] = useState(false);
  const [variacoes, setVariacoes] = useState<Turma[]>([]);
  const [tabLoading, setTabLoading] = useState(false);

  // endpoints possíveis
  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const turmasURLs = [
    '/turmas',
    '/api/turmas',
    `http://${host}:5000/turmas`,
    `http://${host}:5000/api/turmas`,
    'http://localhost:5000/turmas',
    'http://localhost:5000/api/turmas',
  ];

  const planoURLs = (planoId: number | string) => [
    `/plano-curso/${planoId}`,
    `/api/plano-curso/${planoId}`,
    `http://${host}:5000/plano-curso/${planoId}`,
    `http://${host}:5000/api/plano-curso/${planoId}`,
    `http://localhost:5000/plano-curso/${planoId}`,
    `http://localhost:5000/api/plano-curso/${planoId}`,
  ];

  useEffect(() => {
    const fetchCurso = async () => {
      setLoading(true);
      try {
        const data = await getWithFallback(turmasURLs);

        const todasAtivas: Turma[] = data?.turmasAtivas || [];
        const todasEmBreve: Turma[] = data?.turmasEmBreve || [];

        let encontrado = todasAtivas.find((c) => String(c.id) === String(id)) || null;
        let emBreve = false;
        if (!encontrado) {
          encontrado = todasEmBreve.find((c) => String(c.id) === String(id)) || null;
          emBreve = Boolean(encontrado);
        }

        if (!encontrado) {
          navigate('/404');
          return;
        }

        setCurso(encontrado);
        setIsTurmaEmBreve(emBreve);

        const state = (location.state || {}) as { variacoes?: Turma[] };
        if (Array.isArray(state.variacoes) && state.variacoes.length) {
          const semDuplicatas = state.variacoes.filter((v) => String(v.id) !== String(id));
          setVariacoes(semDuplicatas);
        } else {
          setVariacoes([]);
        }

        // Só busca matriz para turmas ativas (não-manual)
        if (!emBreve && activeTab === 2 && encontrado?.planoDoCursoId && matrizCurricular.length === 0) {
          await fetchMatrizCurricular(encontrado.planoDoCursoId);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar curso:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => {
    const tentarCarregarMatriz = async () => {
      if (!isTurmaEmBreve && activeTab === 2 && curso?.planoDoCursoId && matrizCurricular.length === 0) {
        await fetchMatrizCurricular(curso.planoDoCursoId);
      }
    };
    tentarCarregarMatriz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, curso, isTurmaEmBreve]);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabLoading(true);
    setTimeout(() => {
      setActiveTab(newValue);
      setTabLoading(false);
    }, 200);
  };

  const fetchMatrizCurricular = async (planoDoCursoId: number) => {
    try {
      setLoadingMatriz(true);
      const data = await getWithFallback(planoURLs(planoDoCursoId));
      const unidadesOrdenadas: UnidadeCurricular[] =
        data?.data?.ordemDaUnidadeCurricular?.sort(
          (a: UnidadeCurricular, b: UnidadeCurricular) => a.ordem - b.ordem
        ) || [];
      setMatrizCurricular(unidadesOrdenadas);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar matriz curricular:', error);
    } finally {
      setLoadingMatriz(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!curso) {
    return (
      <Typography sx={{ textAlign: 'center', py: 6 }} color="error">
        Curso não encontrado
      </Typography>
    );
  }

  const formas = curso?.ofertas?.[0]?.formasDePagamento || [];
  const horarios = curso?.horariosSemAbreviacao?.join(' | ') || 'A definir';
  const duracao = curso?.dataPeriodoExecucaoInicialFormatada
    ? `De ${curso.dataPeriodoExecucaoInicialFormatada} até ${curso.dataPeriodoExecucaoFinalFormatada}`
    : 'A definir';

  const linkMatricula =
    curso?.linkInscricao ||
    curso?.recursosFinanceiros?.[0]?.linkMatriculaOnlineDoRecursoFinanceiroNovo ||
    (curso as any)?.linkMatriculaOnline ||
    null;

  const ehPos = isPosGraduacao(curso);

  // ====================
  // RENDERIZAÇÃO
  // ====================
  return (
    <Container sx={{ py: 5 }}>
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 4, bgcolor: '#2b6cb0', '&:hover': { bgcolor: '#2c5282' } }}
      >
        Voltar para Cursos
      </Button>

      <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>
        {curso.nomeTurma}
      </Typography>

      {/* ===================== */}
      {/* CURSO MANUAL (EM BREVE) */}
      {/* ===================== */}
      {isTurmaEmBreve ? (
        (() => {
          const isPos = ehPos;
          const eadLink = curso.linkInscricaoEAD || curso.linkInscricao || null;

          // Pós/EAD manual: mostra texto padronizado + botão de inscrição externo
          if (isPos && eadLink) {
            return (
              <Paper elevation={2} sx={{ p: 4, mb: 5 }}>
                <Typography variant="h6" gutterBottom>Sobre o Curso (Pós-Graduação EAD)</Typography>
                <Typography sx={{ mb: 2 }}>
                  Este curso de Pós-Graduação é ofertado na modalidade <strong>EAD</strong> por meio de nossa
                  plataforma parceira. A inscrição e a matrícula são realizadas diretamente no portal do curso EAD.
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Após concluir a inscrição, você receberá as instruções de acesso ao ambiente virtual de aprendizagem.
                  Caso tenha dúvidas, nossa equipe de atendimento está à disposição.
                </Typography>

                <Divider sx={{ my: 3 }} />
                <Typography><strong>Modalidade:</strong> {curso.formaDeExecucao || 'EAD'}</Typography>
                {/* Horários REMOVIDOS para Pós/EAD */}
                <Typography><strong>Duração:</strong> {duracao}</Typography>

                <Button
                  variant="contained"
                  sx={{ mt: 3, backgroundColor: '#2b6cb0', '&:hover': { backgroundColor: '#2c5282' } }}
                  href={eadLink}
                  target="_blank"
                >
                  Inscreva-se no Portal EAD
                </Button>
              </Paper>
            );
          }

          // Demais manuais: mantém fluxo de "interesse"
          return (
            <Paper elevation={2} sx={{ p: 4, mb: 5 }}>
              <Typography variant="h6" gutterBottom>Sobre o Curso</Typography>
              <Box
                dangerouslySetInnerHTML={{ __html: curso.informacoesParaVendaOnline || 'Informação não disponível' }}
              />

              <Typography variant="h6" sx={{ mt: 3 }}>Objetivos</Typography>
              <Box
                dangerouslySetInnerHTML={{ __html: curso.informacoesDoPlanoDoCurso?.objetivos || 'Informação não disponível' }}
              />

              <Typography variant="h6" sx={{ mt: 3 }}>Perfil Profissional</Typography>
              <Box
                dangerouslySetInnerHTML={{ __html: curso.informacoesDoPlanoDoCurso?.perfilProfissional || 'Informação não disponível' }}
              />

              <Divider sx={{ my: 3 }} />
              <Typography><strong>Horários:</strong> {horarios}</Typography>
              <Typography><strong>Duração:</strong> {duracao}</Typography>
              <Typography><strong>Curso:</strong> {curso.formaDeExecucao || 'A definir'}</Typography>

              {curso.linkInteresse && (
                <Button
                  variant="contained"
                  sx={{ mt: 3, backgroundColor: '#247df1ff', '&:hover': { backgroundColor: '#b7791f' } }}
                  href={curso.linkInteresse}
                  target="_blank"
                >
                  Registre seu Interesse
                </Button>
              )}
            </Paper>
          );
        })()
      ) : (
        <>
          {/* ===================== */}
          {/* CURSO ATIVO (ABERTO)  */}
          {/* ===================== */}
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 4 }}
          >
            <Tab label="Sobre o Curso" />
            <Tab label="Mercado de Trabalho" />
            <Tab label="Matriz Curricular" />
            <Tab label="Coordenação" />
            <Tab label="Investimento" />
          </Tabs>

          <Paper elevation={2} sx={{ p: 4, mb: 5, minHeight: 250 }}>
            {tabLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <Fade in timeout={300}>
                <Box>
                  {activeTab === 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Sobre o Curso</Typography>
                      <Box
                        dangerouslySetInnerHTML={{ __html: curso.informacoesParaVendaOnline || 'Informação não disponível' }}
                      />
                      <Typography variant="h6" sx={{ mt: 3 }}>Objetivos</Typography>
                      <Box
                        dangerouslySetInnerHTML={{ __html: curso.informacoesDoPlanoDoCurso?.objetivos || 'Informação não disponível' }}
                      />
                    </Box>
                  )}

                  {activeTab === 1 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Mercado de Trabalho</Typography>
                      <Box
                        dangerouslySetInnerHTML={{ __html: curso.informacoesDoPlanoDoCurso?.perfilProfissional || 'Informação não disponível' }}
                      />
                    </Box>
                  )}

                  {activeTab === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Matriz Curricular</Typography>
                      {loadingMatriz ? (
                        <Box py={2}><CircularProgress /></Box>
                      ) : matrizCurricular.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ordem</TableCell>
                              <TableCell>Unidade Curricular</TableCell>
                              <TableCell>Carga Horária</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matrizCurricular.map((uc) => (
                              <TableRow key={uc.unidadeCurricularId}>
                                <TableCell>{uc.ordem}</TableCell>
                                <TableCell>{uc.nomeDaUnidadeCurricular}</TableCell>
                                <TableCell>{uc.cargaHoraria}h</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography>Matriz curricular não disponível</Typography>
                      )}
                    </Box>
                  )}

                  {activeTab === 3 && (
  <Box>
    <Typography variant="h6" gutterBottom>Coordenação do Curso</Typography>
    {(() => {
      const nomeTurma = curso?.nomeTurma || '';
      const nomePlano = curso?.informacoesDoPlanoDoCurso?.nome || '';
      const coordenador = coordenadores.find((c: any) =>
        nomeTurma.includes(c.nomeCurso) || nomePlano.includes(c.nomeCurso)
      );
      return coordenador ? (
        <Box sx={{ mt: 2 }}>
        <Divider sx={{ my: 2 }} />
        <Typography>
          <strong>E-mail:</strong>{' '}
          <a href="mailto:pos.graduacao@am.senac.br">
            pos.graduacao@am.senac.br
          </a>
        </Typography>
        <Typography>
          <strong>Contato:</strong>{' '}
          <a href="tel:+559231999956">(92) 3199-9956</a>
        </Typography>
      </Box>
      ) : (
        <Typography>Informações de coordenação não disponíveis para este curso</Typography>
      );
    })()}

    {/* Contatos fixos APENAS para cursos de Graduação (não Pós/EAD) */}
    {!ehPos && (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ my: 2 }} />
        <Typography>
          <strong>E-mail:</strong>{' '}
          <a href="mailto:coordenacao.faculdade@am.senac.br">
            coordenacao.faculdade@am.senac.br
          </a>
        </Typography>
        <Typography>
          <strong>Contato:</strong>{' '}
          <a href="tel:+559231999956">(92) 3199-9956</a>
        </Typography>
      </Box>
    )}
  </Box>
)}

                  {activeTab === 4 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Investimento</Typography>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1">Opções de Pagamento no semestre:</Typography>
                        {formas.length > 0 ? (
                          formas.map((forma, index) => (
                            forma?.disponivelParaWeb ? (
                              <Box key={index} sx={{ mb: 2 }}>
                                <Typography fontWeight={600}>{forma.meioDePagamentoDescricao}</Typography>
                                {Number(forma.numeroDeParcelas || 0) > 1 ? (
                                  <>
                                    <Typography>Parcelas: {forma.numeroDeParcelas}x de {currencyBRL(forma.valorDaParcela)}</Typography>
                                    <Typography>Total: {currencyBRL(forma.totalPagamento)}</Typography>
                                  </>
                                ) : (
                                  <Typography>À vista: {currencyBRL(forma.totalPagamento)}</Typography>
                                )}
                                {index < formas.length - 1 && <Divider sx={{ my: 2 }} />}
                              </Box>
                            ) : null
                          ))
                        ) : (
                          <Typography>Informações de investimento não disponíveis.</Typography>
                        )}
                      </Box>

                      {/* Horários ocultos para Pós/EAD */}
                      {!ehPos && (
                        <Typography><strong>Horários:</strong> {horarios}</Typography>
                      )}
                      <Typography><strong>Duração:</strong> {duracao}</Typography>
                      <Typography><strong>Curso:</strong> {curso.formaDeExecucao || 'A definir'}</Typography>

                      {linkMatricula && (
                        <Button
                          variant="contained"
                          sx={{ mt: 3, backgroundColor: '#38a169', '&:hover': { backgroundColor: '#2f855a' } }}
                          href={linkMatricula}
                          target="_blank"
                        >
                          Fazer Matrícula
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Fade>
            )}
          </Paper>

          {/* Se ativo, mantém seção de inscrições por unidade (NÃO exibir para Pós/EAD) */}
          {!ehPos && (
            <Box sx={{ backgroundColor: '#f9fbff', p: 4, borderRadius: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>Inscrições Vestibular Senac AM</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography>
                Inscreva-se agora no vestibular 2025 e comece a trilhar o seu futuro!
                As inscrições vão até 12 de agosto de 2025 — não deixe para a última hora!
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Escolha a unidade mais próxima e clique no botão abaixo para garantir sua participação no processo.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 3 }}>
                {[curso, ...variacoes].filter(Boolean).map((v: Turma) => (
                  <Button
                    key={String(v.id)}
                    variant="contained"
                    href={
                      (v.linkInscricao as string) ||
                      v.recursosFinanceiros?.[0]?.linkMatriculaOnlineDoRecursoFinanceiroNovo ||
                      '#'
                    }
                    target="_blank"
                    sx={{ borderRadius: '24px', backgroundColor: '#E3006D', '&:hover': { backgroundColor: '#9F004D' } }}
                  >
                    Inscrição para a unidade {v.nomeUnidadeOperativa || '—'}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Curso;
